import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { BasketIcon } from '../../BasketIcon';

const EXAMPLES = [
  {
    id: 'ex-1',
    topicLabel: 'Topic X',
    detailLabel: 'painful',
    searchKeyword: 'appendicitis',
    topics: [
      {
        name: 'Appendicitis',
        annotation: "Appendicitis already meant 'painful' to me.",
      },
      { name: 'Topic Y' },
    ],
    ghostNewTopic: {
      topicTitle: 'Topic X',
      linkPhrase: 'painful like appendicitis',
      detailLine: 'painful',
    },
  },
  {
    id: 'ex-2',
    topicLabel: 'Multiple myeloma',
    detailLabel:
      'defined as 10% or more clonal plasma cells on a bone marrow biopsy',
    searchKeyword: '10',
    screamsDetailLabel: '10',
    exampleSlideTitleOverride: 'What screams 10 to me? Cranial nerve 10!',
    topics: [
      { name: 'Vagus nerve (Cranial nerve 10)' },
      { name: 'Topic N' },
    ],
    ghostNewTopic: {
      topicTitle: 'Multiple myeloma',
      detailLine:
        'defined as 10% or more clonal plasma cells on a bone marrow biopsy',
      annotation: '10% like the 10 in Cranial nerve 10',
    },
  },
  {
    id: 'ex-3',
    topicLabel: 'Pelger-Huet Anomaly',
    detailLabel:
      'Caused by heterozygous mutation in the Lamin B receptor (LBR) gene',
    searchKeyword: 'B',
    screamsDetailLabel: 'B',
    exampleSlideTitleOverride: 'What screams B to me? Beta-blocker!',
    topics: [{ name: 'Beta-blocker' }],
    ghostNewTopic: {
      topicTitle: 'Pelger-Huet Anomaly',
      detailLine:
        'Caused by heterozygous mutation in the Lamin B receptor (LBR) gene',
      annotation: 'B receptor like B blocker',
    },
  },
];

const formatSearchKeywordForDisplay = (searchKeyword) => {
  if (!searchKeyword?.trim()) return '';
  const kw = searchKeyword.trim();
  return kw === kw.toUpperCase() ? kw : `${kw.charAt(0).toUpperCase()}${kw.slice(1)}`;
};

const buildScreamsAnnotation = (detailLabel, searchKeyword, screamsDetailLabel) => {
  const detailForScreams = screamsDetailLabel?.trim() || detailLabel?.trim();
  if (!detailForScreams || !searchKeyword?.trim()) return null;
  const displayKeyword = formatSearchKeywordForDisplay(searchKeyword);
  if (screamsDetailLabel?.trim()) {
    return `What screams '${detailForScreams}' to me?`;
  }
  return `What screams '${detailForScreams}' to me? ${displayKeyword}.`;
};

const SearchBarSelfAnnotation = ({ detailLabel, searchKeyword, screamsDetailLabel }) => {
  const detailForScreams = screamsDetailLabel?.trim() || detailLabel?.trim();
  if (!detailForScreams || !searchKeyword?.trim()) return null;
  const displayKeyword = formatSearchKeywordForDisplay(searchKeyword);
  const exampleLine = screamsDetailLabel?.trim()
    ? `What screams '${detailForScreams}' to me?`
    : `What screams '${detailForScreams}' to me? ${displayKeyword}.`;
  return (
    <div className="mt-3 space-y-2" role="note">
      <p className="m-0 text-xs font-bold uppercase tracking-wide text-neutral-700 dark:text-neutral-300">
        The question to ask yourself
      </p>
      <p className="m-0 text-xs italic leading-snug text-neutral-600 dark:text-neutral-400">
        What screams &apos;[insert hard detail here]&apos; to me?
      </p>
      <p className="m-0 text-xs font-medium leading-snug text-neutral-800 dark:text-neutral-200">
        {exampleLine}
      </p>
    </div>
  );
};

const highlightMatchInText = (text, keyword) => {
  if (!keyword || !text) return text;
  const lowerText = text.toLowerCase();
  const lowerKey = keyword.toLowerCase();
  const idx = lowerText.indexOf(lowerKey);
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + keyword.length);
  const after = text.slice(idx + keyword.length);
  return (
    <>
      {before}
      <span className="rounded-sm bg-yellow-200 font-semibold text-neutral-900 dark:bg-yellow-300">{match}</span>
      {after}
    </>
  );
};

const MiniSearchDisplay = ({ keyword }) => (
  <div className="space-y-2">
    <p className="text-xs font-bold uppercase tracking-wide text-cranberry-800 dark:text-cranberry-400">
      Search baskets
    </p>
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" aria-hidden="true" />
      <div
        className="
          flex min-h-[48px] w-full items-center rounded-xl border border-neutral-300 bg-white pl-9 pr-3 text-base
          font-semibold text-neutral-900 shadow-sm ring-1 ring-cranberry-500/50 ring-offset-1 ring-offset-white
          dark:border-neutral-600 dark:bg-neutral-900 dark:ring-cranberry-400/40 dark:ring-offset-neutral-900
        "
        aria-label={`Search keyword: ${keyword}`}
      >
        <span className="break-words">{keyword}</span>
      </div>
    </div>
  </div>
);

const LeftTopicColumn = ({ topicLabel, detailLabel }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-neutral-300 dark:border-neutral-600 p-3 sm:p-4 shadow-sm">
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="text-xs font-bold text-gray-400 dark:text-neutral-500 uppercase tracking-wide flex-shrink-0">
        New Topic to Place
      </span>
    </div>
    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-neutral-100 break-words">
      {topicLabel}
    </h3>
    <div
      className="
        mt-3 max-h-32 overflow-y-auto rounded-r-lg border-l-2 border-amber-400 bg-amber-50 p-4 shadow-sm
        ring-1 ring-amber-300/60 ring-offset-1 ring-offset-white dark:bg-amber-950/30 dark:border-amber-500/70
        dark:ring-amber-500/30 dark:ring-offset-neutral-900
      "
    >
      <p className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">
        Hard Detail
      </p>
      <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-neutral-100 leading-relaxed break-words">
        {detailLabel}
      </p>
    </div>
  </div>
);

const GhostNewTopicGridCell = ({ topicTitle, linkPhrase, detailLine, annotation }) => (
  <li className="flex min-h-0 min-w-0 flex-col gap-2">
    <div
      className="
        flex min-h-20 flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-dashed border-amber-400
        bg-gradient-to-b from-amber-50/90 to-neutral-50/70 px-4 py-4 text-center shadow-sm dark:border-amber-500/60
        dark:from-amber-950/40 dark:to-neutral-900/60
      "
      role="note"
      aria-label="Preview only: this topic is not in the basket yet. It shows how it will look after you add it."
    >
      <span className="text-xs font-semibold uppercase leading-tight tracking-wide text-neutral-500 dark:text-neutral-400">
        Goes here
      </span>
      <span className="text-sm font-bold leading-snug text-neutral-600 dark:text-neutral-300">{topicTitle}</span>
      {linkPhrase ? (
        <span className="text-xs italic leading-snug text-neutral-500 dark:text-neutral-400">{linkPhrase}</span>
      ) : null}
      {detailLine ? (
        <span className="text-xs font-medium leading-snug text-neutral-500 dark:text-neutral-400">{detailLine}</span>
      ) : null}
    </div>
    {annotation ? (
      <p className="px-1 text-center text-xs italic leading-snug text-neutral-500 dark:text-neutral-400">{annotation}</p>
    ) : null}
  </li>
);

const BasketTopicGridItem = ({ topic, searchKeyword }) => (
  <li className="flex min-h-0 min-w-0 flex-col gap-2">
    <div
      className="
        flex min-h-20 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-4
        text-center text-sm font-medium leading-snug text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800
        dark:text-neutral-100
      "
    >
      {highlightMatchInText(topic.name, searchKeyword)}
    </div>
    {topic.annotation ? (
      <p className="px-1 text-center text-xs italic leading-snug text-neutral-500 dark:text-neutral-400">
        {topic.annotation}
      </p>
    ) : null}
  </li>
);

const MiniBasketCard = ({ topics, searchKeyword, isSelected, ghostNewTopic }) => (
  <div
    className={`
      flex h-full min-h-60 flex-col rounded-xl border bg-white p-5 pt-3 shadow-sm dark:bg-neutral-900
      ${isSelected
        ? 'border-cranberry-600 dark:border-cranberry-500/80'
        : 'border-neutral-300 dark:border-neutral-600'
      }
    `}
  >
    <div className="mb-4 border-b border-neutral-200/80 pb-3 dark:border-neutral-700/80">
      <div className="flex items-center gap-2 text-base font-bold text-neutral-900 dark:text-neutral-100">
        <span className="-ml-1 inline-flex shrink-0 [&_svg]:mr-0" aria-hidden="true">
          <BasketIcon />
        </span>
        <span>Basket</span>
      </div>
    </div>
    <ul
      className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 content-start"
      aria-label="Topics in basket"
    >
      {ghostNewTopic ? (
        <>
          {topics[0] ? (
            <BasketTopicGridItem topic={topics[0]} searchKeyword={searchKeyword} />
          ) : null}
          <GhostNewTopicGridCell key="ghost-new-topic" {...ghostNewTopic} />
          {topics.slice(1).map((t) => (
            <BasketTopicGridItem key={t.name} topic={t} searchKeyword={searchKeyword} />
          ))}
        </>
      ) : (
        topics.map((t) => (
          <BasketTopicGridItem key={t.name} topic={t} searchKeyword={searchKeyword} />
        ))
      )}
    </ul>
  </div>
);

const ExampleSlide = ({
  topicLabel,
  detailLabel,
  searchKeyword,
  screamsDetailLabel,
  topics,
  ghostNewTopic,
}) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
    <div className="flex flex-col gap-4 lg:w-2/5">
      <LeftTopicColumn topicLabel={topicLabel} detailLabel={detailLabel} />
      <div>
        <MiniSearchDisplay keyword={searchKeyword} />
        <SearchBarSelfAnnotation
          detailLabel={detailLabel}
          searchKeyword={searchKeyword}
          screamsDetailLabel={screamsDetailLabel}
        />
      </div>
    </div>
    <div className="flex flex-col gap-2 lg:w-3/5 lg:min-w-0">
      <MiniBasketCard
        topics={topics}
        searchKeyword={searchKeyword}
        ghostNewTopic={ghostNewTopic}
        isSelected
      />
    </div>
  </div>
);

const BasketSelectionExamplesModal = ({ isOpen, onClose }) => {
  const closeButtonRef = useRef(null);
  const [exampleIndex, setExampleIndex] = useState(0);

  const lastIndex = EXAMPLES.length - 1;

  const handleExamplePrev = useCallback(() => {
    setExampleIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleExampleNext = useCallback(() => {
    setExampleIndex((i) => Math.min(lastIndex, i + 1));
  }, [lastIndex]);

  const handleExampleDot = useCallback((index) => {
    setExampleIndex(index);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setExampleIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setExampleIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setExampleIndex((i) => Math.min(lastIndex, i + 1));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, lastIndex]);

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const current = EXAMPLES[exampleIndex];
  const slideHeaderText =
    current.exampleSlideTitleOverride ??
    buildScreamsAnnotation(
      current.detailLabel,
      current.searchKeyword,
      current.screamsDetailLabel
    ) ??
    '';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3 sm:p-6"
      onMouseDown={handleBackdropMouseDown}
      role="presentation"
    >
      <div
        className="
          max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-neutral-300
          bg-neutral-50 shadow-2xl dark:border-neutral-600 dark:bg-neutral-900
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="basket-examples-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-neutral-200/90 bg-neutral-50 px-4 py-3 dark:border-neutral-700/80 dark:bg-neutral-900 sm:px-5">
          <div>
            <h2 id="basket-examples-title" className="text-base font-bold text-neutral-900 dark:text-neutral-100">
              Choosing the right basket
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
              Search for a topic that already means your difficult detail to you - not what's textbook-related, but your own personal association. When you spot it, that's your basket.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="
              flex h-9 min-w-9 shrink-0 items-center justify-center rounded-lg border border-neutral-300
              text-lg font-bold leading-none text-neutral-800 transition-colors hover:bg-neutral-200
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700
              dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800
            "
            aria-label="Close examples"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div
            className="relative"
            role="region"
            aria-roledescription="carousel"
            aria-label="Basket selection examples"
            aria-live="polite"
          >
            <h3
              id="basket-examples-slide-label"
              className="mb-4 text-base font-semibold leading-snug text-neutral-900 dark:text-neutral-100"
            >
              {slideHeaderText}
            </h3>
            <p className="sr-only">
              Example {exampleIndex + 1} of {EXAMPLES.length}
            </p>
            <div aria-labelledby="basket-examples-slide-label">
              <ExampleSlide
                key={current.id}
                topicLabel={current.topicLabel}
                detailLabel={current.detailLabel}
                searchKeyword={current.searchKeyword}
                screamsDetailLabel={current.screamsDetailLabel}
                topics={current.topics}
                ghostNewTopic={current.ghostNewTopic}
              />
            </div>

            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={handleExamplePrev}
                disabled={exampleIndex === 0}
                className="
                  flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium
                  text-neutral-900 transition-colors hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40
                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700
                  dark:border-neutral-600 dark:text-neutral-100 dark:hover:bg-neutral-800 sm:w-auto
                "
                aria-label="Previous example"
              >
                <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
                Previous
              </button>

              <div className="flex items-center gap-2" aria-label="Choose example">
                {EXAMPLES.map((ex, index) => {
                  const isActive = index === exampleIndex;
                  return (
                    <button
                      key={ex.id}
                      type="button"
                      aria-label={`Go to example ${index + 1}`}
                      aria-current={isActive ? 'true' : undefined}
                      onClick={() => handleExampleDot(index)}
                      className={`
                        h-2.5 w-2.5 rounded-full transition-colors
                        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700
                        ${isActive
                          ? 'bg-cranberry-700 dark:bg-cranberry-500'
                          : 'bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-600 dark:hover:bg-neutral-500'
                        }
                      `}
                    />
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleExampleNext}
                disabled={exampleIndex === lastIndex}
                className="
                  flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium
                  text-neutral-900 transition-colors hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40
                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700
                  dark:border-neutral-600 dark:text-neutral-100 dark:hover:bg-neutral-800 sm:w-auto
                "
                aria-label="Next example"
              >
                Next
                <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              </button>
            </div>

            <p className="mt-3 text-center text-xs text-neutral-500 dark:text-neutral-400">
              Use arrow keys to move between examples.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasketSelectionExamplesModal;
